import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Mail, Plus, Trash2, Send, Users, FileText, BarChart3, ChevronRight,
  Upload, Eye, Loader2, Sparkles, X, Check, ArrowLeft, Clock, TrendingUp,
  UserMinus, UserCheck, Edit3, List
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Tab = "lists" | "templates" | "campaigns" | "analytics";

interface EmailList {
  id: number;
  workspaceId: number;
  name: string;
  description: string | null;
  contactCount?: number;
  createdAt: string;
}

interface Contact {
  id: number;
  listId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  createdAt: string;
}

interface EmailTemplate {
  id: number;
  workspaceId: number;
  name: string;
  subject: string;
  htmlBody: string;
  aiGenerated: boolean;
  createdAt: string;
}

interface EmailCampaign {
  id: number;
  workspaceId: number;
  listId: number | null;
  templateId: number | null;
  name: string;
  subject: string;
  status: string;
  scheduledAt: string | null;
  sentAt: string | null;
  sentCount: number;
  failedCount: number;
  openCount: number;
  clickCount: number;
  createdAt: string;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    subscribed: "bg-green-500/10 text-green-400 border-green-500/20",
    unsubscribed: "bg-red-500/10 text-red-400 border-red-500/20",
    draft: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    sending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    sent: "bg-green-500/10 text-green-400 border-green-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ListsTab({ wsId }: { wsId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedList, setSelectedList] = useState<EmailList | null>(null);
  const [newListName, setNewListName] = useState("");
  const [newListDesc, setNewListDesc] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactFirst, setNewContactFirst] = useState("");
  const [newContactLast, setNewContactLast] = useState("");
  const [csvText, setCsvText] = useState("");
  const [showImport, setShowImport] = useState(false);

  const { data: lists = [], isLoading } = useQuery<EmailList[]>({
    queryKey: ["/api/workspaces", wsId, "email-lists"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-lists`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const { data: contacts = [], isLoading: contactsLoading } = useQuery<Contact[]>({
    queryKey: ["/api/workspaces", wsId, "email-lists", selectedList?.id, "contacts"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-lists/${selectedList!.id}/contacts`, { credentials: "include" }).then(r => r.json()),
    enabled: !!selectedList,
  });

  const createList = useMutation({
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/email-lists`, { name: newListName, description: newListDesc }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists"] });
      setShowCreate(false);
      setNewListName(""); setNewListDesc("");
      toast({ title: "List created" });
    },
  });

  const deleteList = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/email-lists/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists"] });
      setSelectedList(null);
      toast({ title: "List deleted" });
    },
  });

  const addContact = useMutation({
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/email-lists/${selectedList!.id}/contacts`, {
      email: newContactEmail, firstName: newContactFirst, lastName: newContactLast,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists", selectedList?.id, "contacts"] });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists"] });
      setNewContactEmail(""); setNewContactFirst(""); setNewContactLast("");
      toast({ title: "Contact added" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const removeContact = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/email-lists/${selectedList!.id}/contacts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists", selectedList?.id, "contacts"] });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists"] });
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      apiRequest("PATCH", `/api/workspaces/${wsId}/email-lists/${selectedList!.id}/contacts/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists", selectedList?.id, "contacts"] }),
  });

  const importContacts = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${wsId}/email-lists/${selectedList!.id}/import`, { csv: csvText })).json(),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists", selectedList?.id, "contacts"] });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-lists"] });
      setShowImport(false); setCsvText("");
      toast({ title: `Imported ${data.imported} contacts` });
    },
    onError: (e: any) => toast({ title: "Import failed", description: e.message, variant: "destructive" }),
  });

  if (selectedList) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedList(null)} data-testid="button-back-to-lists">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold">{selectedList.name}</h2>
            {selectedList.description && <p className="text-sm text-muted-foreground">{selectedList.description}</p>}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="button-import-csv">
              <Upload className="h-4 w-4 mr-1" /> Import CSV
            </Button>
            <Button size="sm" variant="destructive" onClick={() => deleteList.mutate(selectedList.id)} data-testid="button-delete-list">
              <Trash2 className="h-4 w-4 mr-1" /> Delete List
            </Button>
          </div>
        </div>

        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Add Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Input placeholder="Email *" value={newContactEmail} onChange={e => setNewContactEmail(e.target.value)} className="flex-1 min-w-[200px]" data-testid="input-contact-email" />
              <Input placeholder="First name" value={newContactFirst} onChange={e => setNewContactFirst(e.target.value)} className="w-36" data-testid="input-contact-first" />
              <Input placeholder="Last name" value={newContactLast} onChange={e => setNewContactLast(e.target.value)} className="w-36" data-testid="input-contact-last" />
              <Button onClick={() => addContact.mutate()} disabled={!newContactEmail || addContact.isPending} data-testid="button-add-contact">
                {addContact.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {contactsLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No contacts yet. Add one above or import a CSV.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-muted-foreground font-medium">Email</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Name</th>
                    <th className="text-left p-3 text-muted-foreground font-medium">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`row-contact-${c.id}`}>
                      <td className="p-3">{c.email}</td>
                      <td className="p-3 text-muted-foreground">{[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}</td>
                      <td className="p-3"><StatusBadge status={c.status} /></td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {c.status === "subscribed" ? (
                            <Button variant="ghost" size="sm" onClick={() => updateStatus.mutate({ id: c.id, status: "unsubscribed" })} data-testid={`button-unsub-${c.id}`}>
                              <UserMinus className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => updateStatus.mutate({ id: c.id, status: "subscribed" })} data-testid={`button-resub-${c.id}`}>
                              <UserCheck className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => removeContact.mutate(c.id)} data-testid={`button-remove-contact-${c.id}`}>
                            <X className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showImport} onOpenChange={setShowImport}>
          <DialogContent>
            <DialogHeader><DialogTitle>Import Contacts from CSV</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Upload a CSV file or paste CSV with columns: email, first_name (optional), last_name (optional). First row can be a header.</p>
              <label
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-6 px-4 cursor-pointer hover:border-blue-500/50 transition-colors bg-muted/20"
                data-testid="label-csv-file-upload"
              >
                <Upload className="h-6 w-6 text-muted-foreground/60" />
                <span className="text-sm font-medium text-foreground/80">Click to upload .csv file</span>
                <span className="text-xs text-muted-foreground/60">or paste data below</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="input-csv-file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setCsvText((ev.target?.result as string) || "");
                    reader.readAsText(file);
                  }}
                />
              </label>
              <Textarea
                placeholder={"email,first_name,last_name\njane@example.com,Jane,Doe\nbob@example.com,Bob,"}
                rows={6}
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                className="font-mono text-xs"
                data-testid="textarea-csv-import"
              />
              <Button className="w-full" onClick={() => importContacts.mutate()} disabled={!csvText || importContacts.isPending} data-testid="button-confirm-import">
                {importContacts.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Import
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manage your subscriber lists</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="button-new-list">
          <Plus className="h-4 w-4 mr-1" /> New List
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : lists.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <List className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-muted-foreground">No lists yet. Create one to get started.</p>
            <Button onClick={() => setShowCreate(true)} data-testid="button-create-first-list">Create your first list</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {lists.map(list => (
            <Card key={list.id} className="bg-card border-border hover:border-blue-500/30 cursor-pointer transition-colors" onClick={() => setSelectedList(list)} data-testid={`card-list-${list.id}`}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <div className="font-medium">{list.name}</div>
                  {list.description && <div className="text-sm text-muted-foreground mt-0.5">{list.description}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-semibold">{list.contactCount ?? 0}</div>
                    <div className="text-xs text-muted-foreground">subscribers</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Email List</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>List Name *</Label>
              <Input placeholder="e.g. Newsletter Subscribers" value={newListName} onChange={e => setNewListName(e.target.value)} data-testid="input-list-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input placeholder="What's this list for?" value={newListDesc} onChange={e => setNewListDesc(e.target.value)} data-testid="input-list-description" />
            </div>
            <Button className="w-full" onClick={() => createList.mutate()} disabled={!newListName || createList.isPending} data-testid="button-confirm-create-list">
              {createList.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create List
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplatesTab({ wsId }: { wsId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [createTab, setCreateTab] = useState<"manual" | "ai">("manual");
  const [editTemplate, setEditTemplate] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", subject: "", htmlBody: "" });
  const [aiForm, setAiForm] = useState({ goal: "", audience: "", tone: "professional" });
  const [aiGenerated, setAiGenerated] = useState<{ subject: string; htmlBody: string } | null>(null);
  const [aiName, setAiName] = useState("");

  const { data: brandKit } = useQuery<any>({
    queryKey: ["/api/workspaces", wsId, "brand-kit"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/brand-kit`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
    enabled: !!wsId,
  });

  const { data: templates = [], isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/workspaces", wsId, "email-templates"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-templates`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const createTemplate = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/workspaces/${wsId}/email-templates`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-templates"] });
      setShowCreate(false);
      setForm({ name: "", subject: "", htmlBody: "" });
      setAiGenerated(null); setAiName("");
      toast({ title: "Template saved" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateTemplate = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PATCH", `/api/workspaces/${wsId}/email-templates/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-templates"] });
      setEditTemplate(null);
      toast({ title: "Template updated" });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/email-templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-templates"] });
      toast({ title: "Template deleted" });
    },
  });

  const generateAI = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${wsId}/email-templates/generate`, aiForm)).json(),
    onSuccess: (data: any) => setAiGenerated(data),
    onError: (e: any) => toast({ title: "AI generation failed", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Reusable email templates for your campaigns</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="button-new-template">
          <Plus className="h-4 w-4 mr-1" /> New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : templates.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <FileText className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-muted-foreground">No templates yet. Create one manually or let AI write it.</p>
            <Button onClick={() => setShowCreate(true)} data-testid="button-create-first-template">Create your first template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {templates.map(t => (
            <Card key={t.id} className="bg-card border-border" data-testid={`card-template-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{t.name}</span>
                      {t.aiGenerated && <Badge className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20">AI</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 truncate">{t.subject}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setPreviewHtml(t.htmlBody)} data-testid={`button-preview-template-${t.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditTemplate(t); setForm({ name: t.name, subject: t.subject, htmlBody: t.htmlBody }); }} data-testid={`button-edit-template-${t.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteTemplate.mutate(t.id)} data-testid={`button-delete-template-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) { setAiGenerated(null); setAiName(""); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Email Template</DialogTitle></DialogHeader>
          <Tabs value={createTab} onValueChange={v => setCreateTab(v as any)}>
            <TabsList className="w-full">
              <TabsTrigger value="manual" className="flex-1" data-testid="tab-manual">Write Manually</TabsTrigger>
              <TabsTrigger value="ai" className="flex-1" data-testid="tab-ai">Generate with AI</TabsTrigger>
            </TabsList>
            <TabsContent value="manual" className="space-y-3 mt-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name" data-testid="input-template-name" /></div>
              <div><Label>Subject Line *</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject" data-testid="input-template-subject" /></div>
              <div>
                <Label>HTML Body *</Label>
                <p className="text-xs text-muted-foreground mb-1">Use {"{{firstName}}"}, {"{{lastName}}"}, {"{{unsubscribe_link}}"} as placeholders</p>
                <Textarea value={form.htmlBody} onChange={e => setForm(f => ({ ...f, htmlBody: e.target.value }))} placeholder="<p>Hi {{firstName}},</p>..." rows={10} className="font-mono text-xs" data-testid="textarea-template-body" />
              </div>
              <Button className="w-full" onClick={() => createTemplate.mutate({ ...form })} disabled={!form.name || !form.subject || !form.htmlBody || createTemplate.isPending} data-testid="button-save-template">
                {createTemplate.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Save Template
              </Button>
            </TabsContent>
            <TabsContent value="ai" className="space-y-3 mt-4">
              {!aiGenerated ? (
                <>
                  <div><Label>Campaign Goal *</Label><Input value={aiForm.goal} onChange={e => setAiForm(f => ({ ...f, goal: e.target.value }))} placeholder="e.g. Promote our summer sale with 20% off" data-testid="input-ai-goal" /></div>
                  <div><Label>Target Audience</Label><Input value={aiForm.audience} onChange={e => setAiForm(f => ({ ...f, audience: e.target.value }))} placeholder="e.g. Small business owners aged 30-50" data-testid="input-ai-audience" /></div>
                  <div>
                    <Label>Tone</Label>
                    <Select value={aiForm.tone} onValueChange={v => setAiForm(f => ({ ...f, tone: v }))}>
                      <SelectTrigger data-testid="select-tone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly & Casual</SelectItem>
                        <SelectItem value="urgent">Urgent & Direct</SelectItem>
                        <SelectItem value="inspirational">Inspirational</SelectItem>
                        <SelectItem value="educational">Educational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {brandKit && (
                    <div className="rounded-lg border border-foreground/[0.06] bg-foreground/[0.02] p-3" data-testid="brand-preview-strip">
                      <p className="text-xs text-muted-foreground mb-2 font-medium">Brand Kit — applied to your email</p>
                      <div className="flex items-center gap-3">
                        {brandKit.logoUrl && (
                          <img src={brandKit.logoUrl} alt="Brand logo" className="h-6 w-auto object-contain rounded" />
                        )}
                        <div className="flex items-center gap-1.5">
                          {brandKit.primaryColor && (
                            <div className="flex items-center gap-1">
                              <div className="h-4 w-4 rounded-full border border-white/10 shadow-sm" style={{ backgroundColor: brandKit.primaryColor }} title="Primary" />
                            </div>
                          )}
                          {brandKit.secondaryColor && (
                            <div className="h-4 w-4 rounded-full border border-white/10 shadow-sm" style={{ backgroundColor: brandKit.secondaryColor }} title="Secondary" />
                          )}
                          {brandKit.accentColor && (
                            <div className="h-4 w-4 rounded-full border border-white/10 shadow-sm" style={{ backgroundColor: brandKit.accentColor }} title="Accent" />
                          )}
                        </div>
                        {brandKit.fontPreference && (
                          <span className="text-xs text-muted-foreground">{brandKit.fontPreference} font</span>
                        )}
                        {brandKit.tagline && (
                          <span className="text-xs text-muted-foreground italic truncate max-w-[120px]">"{brandKit.tagline}"</span>
                        )}
                      </div>
                    </div>
                  )}
                  <Button className="w-full" onClick={() => generateAI.mutate()} disabled={!aiForm.goal || generateAI.isPending} data-testid="button-generate-ai">
                    {generateAI.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate with AI</>}
                  </Button>
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-purple-400" />
                      <span className="text-sm font-medium text-purple-400">AI Generated</span>
                    </div>
                    <div className="text-sm font-medium mb-1">Subject: {aiGenerated.subject}</div>
                    <Button variant="outline" size="sm" onClick={() => setPreviewHtml(aiGenerated.htmlBody)} data-testid="button-preview-ai"><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                  </div>
                  <div><Label>Save as template name *</Label><Input value={aiName} onChange={e => setAiName(e.target.value)} placeholder="Template name" data-testid="input-ai-template-name" /></div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setAiGenerated(null); }} data-testid="button-regenerate">Regenerate</Button>
                    <Button className="flex-1" onClick={() => createTemplate.mutate({ name: aiName, subject: aiGenerated.subject, htmlBody: aiGenerated.htmlBody, aiGenerated: true })} disabled={!aiName || createTemplate.isPending} data-testid="button-save-ai-template">
                      {createTemplate.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                      Save Template
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTemplate} onOpenChange={v => { if (!v) setEditTemplate(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="input-edit-template-name" /></div>
            <div><Label>Subject Line *</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} data-testid="input-edit-template-subject" /></div>
            <div><Label>HTML Body *</Label><Textarea value={form.htmlBody} onChange={e => setForm(f => ({ ...f, htmlBody: e.target.value }))} rows={10} className="font-mono text-xs" data-testid="textarea-edit-template-body" /></div>
            <Button className="w-full" onClick={() => updateTemplate.mutate({ id: editTemplate!.id, data: form })} disabled={!form.name || !form.subject || !form.htmlBody || updateTemplate.isPending} data-testid="button-update-template">
              {updateTemplate.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewHtml} onOpenChange={v => { if (!v) setPreviewHtml(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle>Template Preview</DialogTitle></DialogHeader>
          <div className="border border-border rounded-lg overflow-auto max-h-[60vh] bg-white">
            <iframe srcDoc={previewHtml || ""} className="w-full h-96 border-0" title="Email preview" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampaignsTab({ wsId }: { wsId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", subject: "", listId: "", templateId: "", scheduledAt: "" });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);

  const { data: campaigns = [], isLoading } = useQuery<EmailCampaign[]>({
    queryKey: ["/api/workspaces", wsId, "email-campaigns"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-campaigns`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const { data: lists = [] } = useQuery<EmailList[]>({
    queryKey: ["/api/workspaces", wsId, "email-lists"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-lists`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/workspaces", wsId, "email-templates"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-templates`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const selectedTemplate = templates.find(t => String(t.id) === form.templateId);

  const createCampaign = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/workspaces/${wsId}/email-campaigns`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-campaigns"] });
      setShowCreate(false); setStep(1);
      setForm({ name: "", subject: "", listId: "", templateId: "", scheduledAt: "" });
      toast({ title: "Campaign created" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const sendCampaign = useMutation({
    mutationFn: (id: number) => {
      setSendingId(id);
      return apiRequest("POST", `/api/workspaces/${wsId}/email-campaigns/${id}/send`).then((r) => r.json());
    },
    onSuccess: (data: any) => {
      setSendingId(null);
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-campaigns"] });
      toast({ title: `Approved: ${data.queued ?? data.sentCount} emails queued`, description: "They go out in batches; people without consent or on the do-not-email list are left out." });
    },
    onError: (e: any) => { setSendingId(null); toast({ title: "Send failed", description: e.message, variant: "destructive" }); },
  });

  const deleteCampaign = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/email-campaigns/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "email-campaigns"] });
      toast({ title: "Campaign deleted" });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Send targeted email broadcasts to your lists</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="button-new-campaign">
          <Plus className="h-4 w-4 mr-1" /> New Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <Send className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-muted-foreground">No campaigns yet. Create one to start sending.</p>
            <Button onClick={() => setShowCreate(true)} data-testid="button-create-first-campaign">Create your first campaign</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-muted-foreground font-medium">Campaign</th>
                  <th className="text-left p-3 text-muted-foreground font-medium">Status</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Sent</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Failed</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`row-campaign-${c.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">{c.subject}</div>
                    </td>
                    <td className="p-3"><StatusBadge status={c.status} /></td>
                    <td className="p-3 text-right">{c.sentCount}</td>
                    <td className="p-3 text-right">{c.failedCount > 0 ? <span className="text-red-400">{c.failedCount}</span> : "0"}</td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end">
                        {(c.status === "draft" || c.status === "scheduled") && (
                          <Button size="sm" onClick={() => sendCampaign.mutate(c.id)} disabled={sendingId === c.id} data-testid={`button-send-campaign-${c.id}`}>
                            {sendingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                            Send
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteCampaign.mutate(c.id)} data-testid={`button-delete-campaign-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) { setStep(1); setForm({ name: "", subject: "", listId: "", templateId: "", scheduledAt: "" }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Campaign — Step {step} of 3</DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-3">
              <div><Label>Campaign Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. July Newsletter" data-testid="input-campaign-name" /></div>
              <div><Label>Subject Line *</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject line" data-testid="input-campaign-subject" /></div>
              <div>
                <Label>Send to List *</Label>
                <Select value={form.listId} onValueChange={v => setForm(f => ({ ...f, listId: v }))}>
                  <SelectTrigger data-testid="select-campaign-list"><SelectValue placeholder="Choose a list" /></SelectTrigger>
                  <SelectContent>
                    {lists.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.name} ({l.contactCount ?? 0} subscribers)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Email Template *</Label>
                <Select value={form.templateId} onValueChange={v => setForm(f => ({ ...f, templateId: v }))}>
                  <SelectTrigger data-testid="select-campaign-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                  <SelectContent>
                    {templates.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" disabled={!form.name || !form.subject || !form.listId || !form.templateId} onClick={() => setStep(2)} data-testid="button-campaign-next">
                Next: Preview <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border p-3 bg-muted/20 space-y-1">
                <div className="text-sm"><span className="text-muted-foreground">Campaign:</span> <span className="font-medium">{form.name}</span></div>
                <div className="text-sm"><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{form.subject}</span></div>
                <div className="text-sm"><span className="text-muted-foreground">List:</span> <span className="font-medium">{lists.find(l => String(l.id) === form.listId)?.name}</span></div>
                <div className="text-sm"><span className="text-muted-foreground">Template:</span> <span className="font-medium">{selectedTemplate?.name}</span></div>
              </div>
              {selectedTemplate && (
                <Button variant="outline" className="w-full" onClick={() => setPreviewHtml(selectedTemplate.htmlBody)} data-testid="button-preview-campaign-template">
                  <Eye className="h-4 w-4 mr-1" /> Preview Email
                </Button>
              )}
              <Button className="w-full" onClick={() => setStep(3)} data-testid="button-campaign-next-schedule">
                Next: Schedule <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setStep(1)} data-testid="button-campaign-back">Back</Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div>
                <Label>Schedule Send (optional)</Label>
                <p className="text-xs text-muted-foreground mb-1">Leave blank to save as draft and send manually</p>
                <Input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))} data-testid="input-campaign-schedule" />
              </div>
              <Button className="w-full" onClick={() => createCampaign.mutate({ name: form.name, subject: form.subject, listId: Number(form.listId), templateId: Number(form.templateId), scheduledAt: form.scheduledAt || null })} disabled={createCampaign.isPending} data-testid="button-create-campaign">
                {createCampaign.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                {form.scheduledAt ? "Schedule Campaign" : "Save as Draft"}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setStep(2)} data-testid="button-campaign-back-2">Back</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewHtml} onOpenChange={v => { if (!v) setPreviewHtml(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
          <div className="border border-border rounded-lg overflow-auto max-h-[60vh] bg-white">
            <iframe srcDoc={previewHtml || ""} className="w-full h-96 border-0" title="Email preview" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AnalyticsTab({ wsId }: { wsId: number }) {
  const { data: campaigns = [], isLoading } = useQuery<EmailCampaign[]>({
    queryKey: ["/api/workspaces", wsId, "email-campaigns"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-campaigns`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const { data: lists = [] } = useQuery<EmailList[]>({
    queryKey: ["/api/workspaces", wsId, "email-lists"],
    queryFn: () => fetch(`/api/workspaces/${wsId}/email-lists`, { credentials: "include" }).then(r => r.json()),
    enabled: !!wsId,
  });

  const sentCampaigns = campaigns.filter(c => c.status === "sent");
  const totalSent = sentCampaigns.reduce((s, c) => s + c.sentCount, 0);
  const totalOpens = sentCampaigns.reduce((s, c) => s + c.openCount, 0);
  const totalClicks = sentCampaigns.reduce((s, c) => s + c.clickCount, 0);
  const totalContacts = lists.reduce((s, l) => s + (l.contactCount ?? 0), 0);
  const avgOpenRate = totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : "0.0";

  const stats = [
    { label: "Total Sent", value: totalSent.toLocaleString(), icon: Send, color: "text-blue-400" },
    { label: "Open Rate", value: `${avgOpenRate}%`, icon: TrendingUp, color: "text-green-400" },
    { label: "Total Contacts", value: totalContacts.toLocaleString(), icon: Users, color: "text-purple-400" },
    { label: "Active Lists", value: lists.length.toString(), icon: List, color: "text-amber-400" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <Card key={i} className="bg-card border-border" data-testid={`card-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`h-4 w-4 ${s.color}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <div className="text-2xl font-bold">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="text-sm font-medium">Recent Campaigns</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : sentCampaigns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No sent campaigns yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-muted-foreground font-medium">Campaign</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Sent</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Opens</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Clicks</th>
                  <th className="text-right p-3 text-muted-foreground font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {sentCampaigns.map(c => (
                  <tr key={c.id} className="border-b border-border/50" data-testid={`row-analytics-campaign-${c.id}`}>
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 text-right">{c.sentCount}</td>
                    <td className="p-3 text-right">{c.openCount}</td>
                    <td className="p-3 text-right">{c.clickCount}</td>
                    <td className="p-3 text-right text-muted-foreground text-xs">{c.sentAt ? new Date(c.sentAt).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function EmailMarketing() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [tab, setTab] = useState<Tab>("lists");

  if (!activeWorkspaceId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-muted-foreground">
          <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Select a workspace to get started</p>
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "lists", label: "Lists", icon: Users },
    { id: "templates", label: "Templates", icon: FileText },
    { id: "campaigns", label: "Campaigns", icon: Send },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-8 w-8 rounded-lg bg-blue-600/20 flex items-center justify-center">
            <Mail className="h-4 w-4 text-blue-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Email Marketing</h1>
            <p className="text-xs text-muted-foreground">Rogers · Broadcast campaigns, lists & automation</p>
          </div>
        </div>
        <div className="flex gap-1 mt-3">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? "bg-blue-600/20 text-blue-400" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {tab === "lists" && <ListsTab wsId={activeWorkspaceId} />}
        {tab === "templates" && <TemplatesTab wsId={activeWorkspaceId} />}
        {tab === "campaigns" && <CampaignsTab wsId={activeWorkspaceId} />}
        {tab === "analytics" && <AnalyticsTab wsId={activeWorkspaceId} />}
      </div>
    </div>
  );
}
